import React, { useMemo, useState } from 'react';
import { Copy, Eye, EyeOff, KeyRound, ShieldCheck, Wand2 } from 'lucide-react';
import toast from 'react-hot-toast';

function generatePSK(length: number) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-+=.@#%:,.~';
  const bytes = new Uint32Array(length);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (n) => chars[n % chars.length]).join('');
}

function copy(text: string, label = 'Copied to clipboard') {
  navigator.clipboard.writeText(text);
  toast.success(label);
}

export default function KeyManagement() {
  const [psk, setPsk] = useState('');
  const [show, setShow] = useState(false);
  const [length, setLength] = useState(64);
  const [vpsId, setVpsId] = useState('2.24.114.251');
  const [homeId, setHomeId] = useState('home.example.com');

  const secretsLine = useMemo(() => {
    return `${vpsId || 'VPS_ID'} ${homeId || 'HOME_ID'} : PSK "${psk || 'GENERATE_PSK_FIRST'}"`;
  }, [vpsId, homeId, psk]);

  return (
    <div className="space-y-8">
      <div className="rounded-3xl bg-white p-8 shadow-sm ring-1 ring-gray-200">
        <div className="mb-3 inline-flex items-center rounded-full bg-green-50 px-3 py-1 text-sm font-medium text-green-700">
          <KeyRound className="mr-2 h-4 w-4" /> Site-to-site authentication
        </div>
        <h1 className="text-3xl font-bold text-gray-900">PSK / Secrets</h1>
        <p className="mt-2 max-w-3xl text-gray-600">
          Generate a strong pre-shared key and copy the exact `ipsec.secrets` line needed by the VPS and home peer. This page does not store the generated PSK.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Generate PSK</h2>
          <div className="mt-5 space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="vpsId">VPS peer ID</label>
                <input id="vpsId" value={vpsId} onChange={(e) => setVpsId(e.target.value)} />
              </div>
              <div>
                <label htmlFor="homeId">Home peer ID</label>
                <input id="homeId" value={homeId} onChange={(e) => setHomeId(e.target.value)} />
              </div>
            </div>
            <div>
              <label htmlFor="length">PSK length</label>
              <select id="length" value={length} onChange={(e) => setLength(Number(e.target.value))}>
                <option value={32}>32 characters</option>
                <option value={64}>64 characters - recommended</option>
                <option value={96}>96 characters</option>
                <option value={128}>128 characters</option>
              </select>
            </div>
            <button
              onClick={() => {
                setPsk(generatePSK(length));
                setShow(false);
                toast.success('Generated PSK. Copy it now; it is not stored.');
              }}
              className="inline-flex items-center rounded-lg bg-green-600 px-5 py-3 text-sm font-medium text-white hover:bg-green-700"
            >
              <Wand2 className="mr-2 h-4 w-4" /> Generate secure PSK
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
          <h2 className="flex items-center text-lg font-semibold"><ShieldCheck className="mr-2 h-5 w-5" /> Handling rules</h2>
          <ul className="mt-4 list-disc space-y-2 pl-5 text-sm">
            <li>Do not paste the real PSK into chat or tickets.</li>
            <li>Copy it directly into the VPS and home VPN configs.</li>
            <li>Rotate it if it is exposed, logged, screenshotted, or emailed.</li>
            <li>Use the same PSK on both peers; peer IDs must match the tunnel config.</li>
          </ul>
        </div>
      </div>

      {psk && (
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Generated PSK</h2>
              <p className="mt-1 text-sm text-gray-600">Length: {psk.length} characters. This value only lives in your browser state.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShow(!show)} className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50">
                {show ? <EyeOff className="mr-2 h-4 w-4" /> : <Eye className="mr-2 h-4 w-4" />}
                {show ? 'Hide' : 'Reveal'}
              </button>
              <button onClick={() => copy(psk, 'PSK copied')} className="inline-flex items-center rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700">
                <Copy className="mr-2 h-4 w-4" /> Copy PSK
              </button>
            </div>
          </div>
          <code className="mt-4 block break-all rounded-xl border bg-gray-50 p-4 text-sm text-gray-900">
            {show ? psk : '•'.repeat(psk.length)}
          </code>
        </div>
      )}

      <div className="rounded-2xl bg-slate-950 p-5 text-slate-100 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold">ipsec.secrets line</h2>
          <button onClick={() => copy(secretsLine, 'ipsec.secrets line copied')} className="rounded-lg bg-white/10 p-2 hover:bg-white/20" title="Copy ipsec.secrets line">
            <Copy className="h-4 w-4" />
          </button>
        </div>
        <pre className="overflow-auto whitespace-pre-wrap text-xs leading-5 text-slate-300">{secretsLine}</pre>
      </div>
    </div>
  );
}
